import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Instagram, Youtube, ArrowUpRight } from 'lucide-react';
import { Reveal } from './Reveal';
import CountUp from './CountUp';

const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SocialStats = () => {
  const { t, language } = useLanguage();

  const stats = [
    {
      key: 'tiktok',
      platform: 'TikTok',
      Icon: TikTokIcon,
      to: 126,
      suffix: 'K+',
      url: 'https://www.tiktok.com/@renihamikus',
      note: language === 'hu' ? 'Rövid videók' : 'Short-form',
    },
    {
      key: 'instagram',
      platform: 'Instagram',
      Icon: Instagram,
      to: 85,
      suffix: 'K+',
      url: 'http://instagram.com/renatahamikus',
      note: language === 'hu' ? 'Esztétika' : 'Aesthetic',
    },
    {
      key: 'youtube',
      platform: 'YouTube',
      Icon: Youtube,
      to: 50,
      suffix: 'K+',
      url: 'https://youtube.com/@renatahamikus',
      note: language === 'hu' ? 'Hosszabb tartalom' : 'Long-form',
    },
  ];

  return (
    <section className="relative py-28 md:py-40 px-6 md:px-12 bg-background" data-testid="social-stats-section">
      <div className="max-w-[1480px] mx-auto">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-5">
            <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
              {language === 'hu' ? '01 — Közösség' : '01 — Community'}
            </Reveal>
            <Reveal as="h2" duration={1.1} className="font-heading text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight">
              <span className="block font-light">{language === 'hu' ? 'Egy közösség' : 'A community'}</span>
              <span className="block italic font-extralight text-text-secondary">
                {language === 'hu' ? 'ami inspirál.' : 'that inspires.'}
              </span>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <Reveal as="p" delay={0.15} className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-md">
              {language === 'hu'
                ? '260,000+ embert ölel át három platformon. Minden szám egy ember, egy történet, egy szebb mindennap.'
                : '260,000+ people across three platforms. Every number is a human, a story, a softer everyday.'}
            </Reveal>

            <div className="mt-10 flex items-baseline gap-4">
              <span className="font-heading text-7xl md:text-8xl font-extralight tracking-tight text-text-primary">
                <CountUp to={260} suffix="K+" />
              </span>
              <span className="font-body text-[0.72rem] uppercase tracking-[0.3em] text-text-secondary">
                {t.social.followers} · {language === 'hu' ? 'összesen' : 'total'}
              </span>
            </div>
          </div>
        </div>

        {/* Editorial bento */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {/* TikTok — large */}
          <motion.a
            href={stats[0].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -6 }}
            className="md:col-span-8 md:row-span-2 group relative overflow-hidden rounded-3xl bg-text-primary p-8 md:p-12 min-h-[320px] md:min-h-[460px] flex flex-col justify-between border border-text-primary"
            data-testid="social-stat-tiktok"
          >
            {/* Gold orb */}
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-accent/40 via-accent/10 to-transparent blur-3xl" />
            <div className="absolute inset-0 grain-layer opacity-40" />

            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-3">
                <TikTokIcon size={28} className="text-background" />
                <span className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-background/60">
                  TikTok
                </span>
              </div>
              <span className="font-body text-[0.7rem] uppercase tracking-[0.3em] text-accent">
                @renihamikus
              </span>
            </div>

            <div className="relative">
              <p className="font-heading text-background text-[5rem] md:text-[7rem] lg:text-[9rem] leading-[0.9] font-extralight tracking-tighter">
                <CountUp to={126} suffix="K+" />
              </p>
              <div className="mt-4 md:mt-6 flex items-center justify-between">
                <span className="font-body text-xs uppercase tracking-[0.3em] text-background/70">
                  {t.social.followers}
                </span>
                <span className="inline-flex items-center gap-2 text-background/80 group-hover:text-accent transition-colors">
                  <span className="font-body text-[0.7rem] uppercase tracking-[0.3em]">{language === 'hu' ? 'Megnyitás' : 'Open'}</span>
                  <ArrowUpRight size={16} strokeWidth={1.25} className="group-hover:rotate-45 transition-transform duration-700" />
                </span>
              </div>
            </div>
          </motion.a>

          {/* Instagram — gold */}
          <motion.a
            href={stats[1].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            whileHover={{ y: -6 }}
            className="md:col-span-4 group relative overflow-hidden rounded-3xl p-8 min-h-[220px] flex flex-col justify-between bg-gradient-to-br from-[#D8C49A] via-[#E8D6B0] to-[#C5A059] border border-accent/40"
            data-testid="social-stat-instagram"
          >
            <div className="absolute inset-0 grain-layer opacity-50" />
            <div className="relative flex items-center justify-between">
              <Instagram size={24} className="text-text-primary" strokeWidth={1.5} />
              <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-primary/70">
                Instagram
              </span>
            </div>
            <div className="relative">
              <p className="font-heading text-text-primary text-6xl md:text-7xl font-extralight tracking-tight">
                <CountUp to={85} suffix="K+" />
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-body text-[0.65rem] uppercase tracking-[0.25em] text-text-primary/70">
                  @renatahamikus
                </span>
                <ArrowUpRight size={14} strokeWidth={1.25} className="text-text-primary/70 group-hover:rotate-45 transition-transform duration-700" />
              </div>
            </div>
          </motion.a>

          {/* YouTube — cream outline */}
          <motion.a
            href={stats[2].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            whileHover={{ y: -6 }}
            className="md:col-span-4 group relative overflow-hidden rounded-3xl bg-surface border border-border p-8 min-h-[220px] flex flex-col justify-between"
            data-testid="social-stat-youtube"
          >
            <div className="absolute inset-0 grain-layer opacity-25" />
            <div className="relative flex items-center justify-between">
              <Youtube size={24} className="text-text-primary" strokeWidth={1.5} />
              <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-secondary">
                YouTube
              </span>
            </div>
            <div className="relative">
              <p className="font-heading text-text-primary text-6xl md:text-7xl font-extralight tracking-tight">
                <CountUp to={50} suffix="K+" />
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-body text-[0.65rem] uppercase tracking-[0.25em] text-text-secondary">
                  @renatahamikus
                </span>
                <ArrowUpRight size={14} strokeWidth={1.25} className="text-text-secondary group-hover:text-accent group-hover:rotate-45 transition-all duration-700" />
              </div>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default SocialStats;
